import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

const NAV = [
  { label: 'Dashboard',       path: '/admin/dashboard',       icon: <DashIcon /> },
  { label: 'Users',           path: '/admin/users',           icon: <UsersIcon /> },
  { label: 'Interpretations', path: '/admin/interpretations', icon: <LabIcon /> },
  { label: 'Unmatched Tests', path: '/admin/unmatched',       icon: <WarnIcon /> },
  { label: 'AI Monitor',      path: '/admin/ai',              icon: <AIIcon /> },
  { label: 'Test Aliases',    path: '/admin/aliases',         icon: <AliasIcon /> },
  { label: 'Settings',        path: '/admin/settings',        icon: <SettingsIcon /> },
];

const ROLE_COLORS = {
  super_admin:    { bg: '#1e3a5f', text: '#93c5fd' },
  trusted_admin:  { bg: '#1e3a5f', text: '#86efac' },
  support_agent:  { bg: '#14532d', text: '#86efac' },
};

const ROLE_LABELS = {
  super_admin:   'Super Admin',
  trusted_admin: 'Trusted Admin',
  support_agent: 'Support Agent',
};

const SIDEBAR_W   = 220;
const COLLAPSED_W = 60;

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const adminInfo = JSON.parse(localStorage.getItem('hrv_admin_info') || '{}');
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('admin_sidebar_collapsed') === '1');

  useEffect(() => {
    localStorage.setItem('admin_sidebar_collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  const handleLogout = () => {
    localStorage.removeItem('hrv_admin_token');
    localStorage.removeItem('hrv_admin_info');
    navigate('/admin/login');
  };

  const rc = ROLE_COLORS[adminInfo.role] || ROLE_COLORS.support_agent;
  const sideW = collapsed ? COLLAPSED_W : SIDEBAR_W;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f4f8' }}>
      {/* ── Sidebar ── */}
      <aside style={{
        width: sideW, flexShrink: 0,
        background: '#1a202c',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
        boxShadow: '3px 0 16px rgba(0,0,0,0.25)',
        transition: 'width 0.2s ease',
        overflow: 'hidden',
      }}>
        {/* Logo + toggle */}
        <div style={{ padding: '18px 14px 16px', borderBottom: '1px solid #2d3748', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '64px', flexShrink: 0 }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', overflow: 'hidden' }}>
              <ShieldIcon />
              <div>
                <div style={{ color: 'white', fontWeight: '800', fontSize: '1rem', lineHeight: 1, letterSpacing: '-0.3px', whiteSpace: 'nowrap' }}>HRV Admin</div>
                <div style={{ color: '#718096', fontSize: '0.68rem', marginTop: '2px', whiteSpace: 'nowrap' }}>Platform Console</div>
              </div>
            </div>
          )}
          {collapsed && <ShieldIcon style={{ margin: '0 auto' }} />}
          <button
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', color: '#718096',
              padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center',
              flexShrink: 0, marginLeft: collapsed ? 'auto' : '0',
            }}
          >
            <CollapseIcon collapsed={collapsed} />
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 0', overflowY: 'auto', overflowX: 'hidden' }}>
          {NAV.map(item => (
            <NavLink key={item.path} to={item.path} title={collapsed ? item.label : undefined}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center',
                gap: collapsed ? 0 : '11px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                padding: collapsed ? '11px 0' : '10px 14px',
                margin: '1px 8px', borderRadius: '8px',
                textDecoration: 'none', fontSize: '0.875rem', fontWeight: '500',
                background: isActive ? '#2b6cb0' : 'transparent',
                color: isActive ? 'white' : '#a0aec0',
                transition: 'all 0.12s',
                whiteSpace: 'nowrap', overflow: 'hidden',
              })}
            >
              <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div style={{ padding: collapsed ? '12px 8px' : '14px', borderTop: '1px solid #2d3748', flexShrink: 0 }}>
          {!collapsed && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ color: 'white', fontSize: '0.82rem', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px' }}>
                {adminInfo.name || adminInfo.email || 'Admin'}
              </div>
              <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: '20px', fontSize: '0.68rem', fontWeight: '700', background: rc.bg, color: rc.text, border: '1px solid #2d4a6e' }}>
                {ROLE_LABELS[adminInfo.role] || adminInfo.role}
              </span>
            </div>
          )}
          <button onClick={handleLogout} title="Sign out" style={{
            width: '100%', padding: collapsed ? '8px 0' : '8px',
            background: '#2d3748', color: '#a0aec0', border: 'none',
            borderRadius: '8px', fontSize: '0.82rem', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          }}
            onMouseOver={e => e.currentTarget.style.background = '#4a5568'}
            onMouseOut={e => e.currentTarget.style.background = '#2d3748'}
          >
            <LogoutIcon />
            {!collapsed && 'Sign Out'}
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main style={{ marginLeft: sideW, flex: 1, padding: '28px 32px', minHeight: '100vh', transition: 'margin-left 0.2s ease' }}>
        {children}
      </main>
    </div>
  );
}

// ── SVG icons (no emojis) ──────────────────────────────────────────────────────

function ShieldIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7L12 2z" fill="#2b6cb0" stroke="#4299e1" strokeWidth="0.5" />
      <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CollapseIcon({ collapsed }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {collapsed
        ? <><path d="M9 18l6-6-6-6" /></>
        : <><path d="M15 18l-6-6 6-6" /></>
      }
    </svg>
  );
}

function DashIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function LabIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v11" />
      <path d="M3 9h18M3 13h18M3 17h18M3 21h18" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function AIIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" />
    </svg>
  );
}

function AliasIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 7 4 4 20 4 20 7" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="12" y1="4" x2="12" y2="20" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}
